import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Upload, X, CheckCircle, Car, Zap, Truck,
  Camera, Loader2, Phone, MapPin,
  ChevronRight, Shield, ArrowLeft, Hash,
  FileCheck, AlertTriangle, Calendar, Edit3,
  CheckCircle2, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = [
  'Seller & Vehicle Details',
  'Start Travel',
  'Confirm Arrival',
  'Take Selfie',
  'Vehicle Photos',
  'Insurance & PUC Verification',  // NEW step 6
  'Verification Form',
  'Owner Photo'
];

// ==================== CHECKLIST FORMS ====================
const ChecklistForm = ({ fields, data, onChange }) => {
  const [remarks, setRemarks] = useState(data.remarks || {});
  return (
    <div className="space-y-6">
      {fields.map(section => (
        <div key={section.section}>
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">{section.section}</h3>
          <div className="space-y-3">
            {section.items.map(item => (
              <div key={item} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex gap-3 items-center mt-0.5 flex-shrink-0">
                  {['ok', 'issue', 'na'].map(val => (
                    <label key={val} className="flex items-center gap-1 cursor-pointer text-xs">
                      <input type="radio" name={`${section.section}-${item}`} value={val}
                        checked={data[item] === val}
                        onChange={() => onChange(item, val)}
                        className={val === 'ok' ? 'accent-green-500' : val === 'issue' ? 'accent-red-500' : 'accent-gray-400'} />
                      <span className={val === 'ok' ? 'text-green-600 font-medium' : val === 'issue' ? 'text-red-600 font-medium' : 'text-gray-500 font-medium'}>
                        {val === 'ok' ? 'OK' : val === 'issue' ? 'Issue' : 'N/A'}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{item}</p>
                  {data[item] === 'issue' && (
                    <Input placeholder="Describe the issue..."
                      className="mt-1 text-xs h-8"
                      value={remarks[item] || ''}
                      onChange={e => {
                        const newR = { ...remarks, [item]: e.target.value };
                        setRemarks(newR);
                        onChange('remarks', newR);
                      }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div>
        <Label className="text-sm font-bold">Overall Notes / Additional Observations</Label>
        <textarea rows={3}
          className="w-full mt-2 p-3 border rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-gray-300"
          placeholder="Any additional observations about the vehicle..."
          value={data.overall_notes || ''}
          onChange={e => onChange('overall_notes', e.target.value)} />
      </div>
    </div>
  );
};

const getChecklistFields = (vehicleType, evSubtype) => {
  if (vehicleType === 'car') return [
    { section: 'Exterior', items: ['Body condition (scratches/dents)', 'Paint quality & color match', 'Glass & windshield condition', 'Headlights & taillights working', 'Wipers functional', 'Tyres condition & tread depth'] },
    { section: 'Interior', items: ['Seats & upholstery condition', 'Dashboard & controls working', 'AC functioning', 'Infotainment/audio system', 'All windows & locks working', 'Odometer reading verified'] },
    { section: 'Mechanical', items: ['Engine noise & performance', 'Brakes functioning', 'Gear shifting smooth', 'Suspension check', 'Steering alignment', 'Battery condition'] },
    { section: 'Documents', items: ['RC book verified', 'Insurance valid', 'PUC certificate valid', 'Service history available'] }
  ];
  if (vehicleType === 'bike') return [
    { section: 'Exterior', items: ['Frame condition (no bends/cracks)', 'Paint quality', 'Headlight & taillight working', 'Mirrors intact', 'Tyres condition'] },
    { section: 'Mechanical', items: ['Engine noise', 'Brakes (front & rear)', 'Chain & sprocket condition', 'Clutch & throttle response', 'Suspension working'] },
    { section: 'Electrical', items: ['Battery condition', 'All indicators working', 'Horn working', 'Speedometer working'] },
    { section: 'Documents', items: ['RC book verified', 'Insurance valid', 'PUC valid'] }
  ];
  if (vehicleType === 'ev') return [
    { section: 'Battery & Charging', items: ['Battery health indicator', 'Charging port condition (AC/DC)', 'Onboard charger working', 'Estimated range verified'] },
    { section: 'Exterior', items: ['Body condition', 'Paint quality', 'Glass condition', 'Headlights & taillights'] },
    { section: 'Interior', items: ['Seats & upholstery', 'Touchscreen/infotainment', 'AC & climate control', 'Sunroof (if any)'] },
    { section: 'Mechanical', items: ['Motor noise', 'Brakes (regen + hydraulic)', 'Suspension', 'Steering response'] },
    { section: 'Documents', items: ['RC book', 'Insurance', 'Warranty remaining', 'Service history'] }
  ];
  if (vehicleType === 'commercial') return [
    { section: 'Exterior & Structure', items: ['Cabin condition', 'Body/cargo area condition', 'Frame integrity', 'Tyres (all axles)', 'Lights & indicators'] },
    { section: 'Mechanical', items: ['Engine condition & noise', 'Brakes (including air brakes)', 'Gearbox condition', 'Clutch', 'Suspension & chassis'] },
    { section: 'Electrical', items: ['Battery & alternator', 'Dashboard instruments', 'Horn & wipers'] },
    { section: 'Documents', items: ['RC book', 'Fitness certificate', 'Insurance', 'Permit (if applicable)', 'PUC'] }
  ];
  return getChecklistFields('car', null);
};

// ==================== PHOTO CAPTURE BOX ====================
const PhotoCaptureBox = ({ label, desc, icon, value, onCapture, onClear }) => (
  <div>
    {value ? (
      <div className="relative rounded-2xl overflow-hidden">
        <img src={value} alt={label} className="w-full h-44 object-cover" />
        <button onClick={onClear}
          className="absolute top-3 right-3 w-9 h-9 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md">
          <X className="h-4 w-4" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-3 py-2 flex items-center gap-1.5">
          <CheckCircle className="h-3.5 w-3.5 text-green-400" /> {label}
        </div>
      </div>
    ) : (
      <label className="cursor-pointer block border-2 border-dashed border-gray-300 rounded-2xl p-5 hover:border-orange-400 transition">
        <div className="flex items-center gap-4">
          <span className="text-3xl">{icon}</span>
          <div className="flex-1">
            <p className="font-semibold text-gray-700 text-sm">{label}</p>
            <p className="text-xs text-gray-500">{desc}</p>
          </div>
          <Camera className="h-6 w-6 text-gray-400" />
        </div>
        <Input type="file" accept="image/*" capture="environment" className="hidden"
          onChange={e => {
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onloadend = () => onCapture(reader.result);
            reader.readAsDataURL(file);
          }} />
      </label>
    )}
  </div>
);

// ==================== INSURANCE & PUC VERIFICATION STEP ====================
const InsurancePUCVerificationStep = ({ vehicle, insurancePUCData, setInsurancePUCData }) => {
  const sellerInsuranceCompany = vehicle?.insurance_company || null;
  const sellerInsuranceExpiry = vehicle?.insurance_expiry || null;
  const sellerHasInsurance = vehicle?.has_insurance;
  const sellerPucExpiry = vehicle?.puc_expiry || null;
  const sellerHasPuc = vehicle?.has_puc;

  const handlePhotoCapture = (field, value) => {
    setInsurancePUCData(prev => ({ ...prev, [field]: value }));
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Insurance & PUC Validation</p>
          <p className="text-xs text-blue-600 mt-0.5">Compare the seller's declared data with the actual documents. Upload photos of the physical documents and correct any mismatches.</p>
        </div>
      </div>

      {/* ── INSURANCE SECTION ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="bg-blue-600 px-5 py-3 flex items-center gap-2">
          <Shield className="h-5 w-5 text-white" />
          <h3 className="font-bold text-white">Vehicle Insurance</h3>
        </div>
        <div className="p-5 space-y-5">
          {/* Seller's declared data */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-gray-500 uppercase mb-3">📋 Seller's Declared Data</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400">Has Insurance</p>
                <p className={`text-sm font-bold mt-0.5 ${sellerHasInsurance ? 'text-green-600' : 'text-red-600'}`}>
                  {sellerHasInsurance === true ? '✅ Yes' : sellerHasInsurance === false ? '❌ No' : '⚪ Not specified'}
                </p>
              </div>
              {sellerHasInsurance && (
                <>
                  <div>
                    <p className="text-xs text-gray-400">Insurance Company</p>
                    <p className="text-sm font-bold mt-0.5 text-gray-800">{sellerInsuranceCompany || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-400">Declared Expiry Date</p>
                    <p className="text-sm font-bold mt-0.5 text-gray-800">
                      {sellerInsuranceExpiry ? new Date(sellerInsuranceExpiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Photo upload for insurance paper */}
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase mb-2">📷 Upload Insurance Document Photo</p>
            <PhotoCaptureBox
              label="Insurance Paper / Policy Document"
              desc="Capture the physical insurance document clearly"
              icon="🛡️"
              value={insurancePUCData.insurance_photo}
              onCapture={v => handlePhotoCapture('insurance_photo', v)}
              onClear={() => handlePhotoCapture('insurance_photo', null)}
            />
          </div>

          {/* Partner's validation */}
          <div className="space-y-4">
            <p className="text-xs font-bold text-gray-700 uppercase">✅ Partner Validation</p>

            {/* Is insurance valid? */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Does this vehicle have active insurance? (as per document)</p>
              <div className="flex gap-3">
                {[true, false].map(v => (
                  <button key={String(v)} type="button"
                    onClick={() => setInsurancePUCData(prev => ({ ...prev, insurance_valid: v, insurance_mismatch: v !== sellerHasInsurance }))}
                    className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all border-2 ${
                      insurancePUCData.insurance_valid === v
                        ? v ? 'bg-green-500 border-green-500 text-white' : 'bg-red-500 border-red-500 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}>
                    {v ? '✓ Yes — Insured' : '✗ No — Not Insured'}
                  </button>
                ))}
              </div>
              {insurancePUCData.insurance_valid === true && insurancePUCData.insurance_valid !== sellerHasInsurance && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700">⚠️ Mismatch with seller's data</p>
                </div>
              )}
            </div>

            {insurancePUCData.insurance_valid === true && (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4 overflow-hidden">
                  {/* Insurance company verification */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase mb-1 block">Insurance Company (as per document)</Label>
                    <Input
                      value={insurancePUCData.verified_insurance_company || sellerInsuranceCompany || ''}
                      onChange={e => setInsurancePUCData(prev => ({
                        ...prev,
                        verified_insurance_company: e.target.value,
                        insurance_company_mismatch: e.target.value !== sellerInsuranceCompany
                      }))}
                      placeholder="Enter company name as shown on document"
                      className="h-10"
                    />
                    {insurancePUCData.verified_insurance_company && insurancePUCData.verified_insurance_company !== sellerInsuranceCompany && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Mismatch: Seller said "{sellerInsuranceCompany}"
                      </p>
                    )}
                  </div>

                  {/* Insurance expiry verification */}
                  <div>
                    <Label className="text-xs text-gray-500 uppercase mb-1 block">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Actual Insurance Expiry Date</span>
                    </Label>
                    <input
                      type="date"
                      value={insurancePUCData.verified_insurance_expiry || sellerInsuranceExpiry || ''}
                      onChange={e => setInsurancePUCData(prev => ({
                        ...prev,
                        verified_insurance_expiry: e.target.value,
                        insurance_expiry_mismatch: e.target.value !== sellerInsuranceExpiry
                      }))}
                      className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    {insurancePUCData.verified_insurance_expiry && insurancePUCData.verified_insurance_expiry !== sellerInsuranceExpiry && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Mismatch: Seller said {sellerInsuranceExpiry ? new Date(sellerInsuranceExpiry).toLocaleDateString('en-IN') : '—'}
                      </p>
                    )}
                    {insurancePUCData.verified_insurance_expiry && insurancePUCData.verified_insurance_expiry === sellerInsuranceExpiry && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Matches seller's data
                      </p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* ── PUC SECTION ── */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="bg-green-600 px-5 py-3 flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-white" />
          <h3 className="font-bold text-white">PUC Certificate</h3>
          <span className="text-xs bg-white/20 text-green-100 px-2 py-0.5 rounded-full">Pollution Under Control</span>
        </div>
        <div className="p-5 space-y-5">
          {/* Seller's declared data */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-gray-500 uppercase mb-3">📋 Seller's Declared Data</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400">Has Valid PUC</p>
                <p className={`text-sm font-bold mt-0.5 ${sellerHasPuc ? 'text-green-600' : 'text-red-600'}`}>
                  {sellerHasPuc === true ? '✅ Yes' : sellerHasPuc === false ? '❌ No' : '⚪ Not specified'}
                </p>
              </div>
              {sellerHasPuc && (
                <div>
                  <p className="text-xs text-gray-400">PUC Expiry Date</p>
                  <p className="text-sm font-bold mt-0.5 text-gray-800">
                    {sellerPucExpiry ? new Date(sellerPucExpiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* PUC Photo Upload */}
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase mb-2">📷 Upload PUC Certificate Photo</p>
            <PhotoCaptureBox
              label="PUC Certificate"
              desc="Capture the PUC certificate clearly showing expiry date"
              icon="📄"
              value={insurancePUCData.puc_photo}
              onCapture={v => handlePhotoCapture('puc_photo', v)}
              onClear={() => handlePhotoCapture('puc_photo', null)}
            />
          </div>

          {/* Partner validation */}
          <div className="space-y-4">
            <p className="text-xs font-bold text-gray-700 uppercase">✅ Partner Validation</p>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Does this vehicle have a valid PUC certificate? (as per document)</p>
              <div className="flex gap-3">
                {[true, false].map(v => (
                  <button key={String(v)} type="button"
                    onClick={() => setInsurancePUCData(prev => ({ ...prev, puc_valid: v, puc_mismatch: v !== sellerHasPuc }))}
                    className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all border-2 ${
                      insurancePUCData.puc_valid === v
                        ? v ? 'bg-green-500 border-green-500 text-white' : 'bg-red-500 border-red-500 text-white'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}>
                    {v ? '✓ Yes — PUC Valid' : '✗ No — No PUC'}
                  </button>
                ))}
              </div>
            </div>

            {insurancePUCData.puc_valid === true && (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden">
                  <Label className="text-xs text-gray-500 uppercase mb-1 block">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Actual PUC Expiry Date</span>
                  </Label>
                  <input
                    type="date"
                    value={insurancePUCData.verified_puc_expiry || sellerPucExpiry || ''}
                    onChange={e => setInsurancePUCData(prev => ({
                      ...prev,
                      verified_puc_expiry: e.target.value,
                      puc_expiry_mismatch: e.target.value !== sellerPucExpiry
                    }))}
                    className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  {insurancePUCData.verified_puc_expiry && insurancePUCData.verified_puc_expiry !== sellerPucExpiry && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Mismatch: Seller said {sellerPucExpiry ? new Date(sellerPucExpiry).toLocaleDateString('en-IN') : '—'}
                    </p>
                  )}
                  {insurancePUCData.verified_puc_expiry && insurancePUCData.verified_puc_expiry === sellerPucExpiry && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Matches seller's data
                    </p>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* Summary of mismatches */}
      {(insurancePUCData.insurance_company_mismatch || insurancePUCData.insurance_expiry_mismatch || insurancePUCData.puc_expiry_mismatch) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Mismatches Detected
          </p>
          <ul className="text-xs text-amber-700 space-y-1">
            {insurancePUCData.insurance_company_mismatch && <li>• Insurance company name differs from seller's declaration</li>}
            {insurancePUCData.insurance_expiry_mismatch && <li>• Insurance expiry date differs from seller's declaration</li>}
            {insurancePUCData.puc_expiry_mismatch && <li>• PUC expiry date differs from seller's declaration</li>}
          </ul>
          <p className="text-xs text-amber-600 mt-2">Your corrected data has been recorded and will be shown on the vehicle detail page.</p>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN INSPECTION PAGE ====================
const PartnerInspectPage = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [step, setStep] = useState(1);
  const [vehicle, setVehicle] = useState(null);
  const [verificationForm, setVerificationForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [travelStartTime, setTravelStartTime] = useState(null);
  const [arrivedTime, setArrivedTime] = useState(null);
  const [selfiePhoto, setSelfiePhoto] = useState(null);
  const [vehiclePhotos, setVehiclePhotos] = useState({ full: null, plate: null, odometer: null });
  const [ownerPhoto, setOwnerPhoto] = useState(null);
  const [checklistData, setChecklistData] = useState({});
  const [formAnswers, setFormAnswers] = useState({});

  // NEW: Insurance & PUC verification state
  const [insurancePUCData, setInsurancePUCData] = useState({
    insurance_photo: null,
    puc_photo: null,
    insurance_valid: null,
    puc_valid: null,
    verified_insurance_company: '',
    verified_insurance_expiry: '',
    verified_puc_expiry: '',
    insurance_company_mismatch: false,
    insurance_expiry_mismatch: false,
    puc_mismatch: false,
    puc_expiry_mismatch: false,
  });

  const TOTAL_STEPS = 8; // added step 6

  useEffect(() => {
    if (!user || user.role !== 'partner') { navigate('/login'); return; }
    fetchData();
  }, [vehicleId, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/partner/assigned-vehicles`, { headers: { Authorization: `Bearer ${token}` } });
      const found = (r.data.vehicles || []).find(v => v.id === vehicleId);
      if (!found) { toast.error('Vehicle not found or not assigned to you'); navigate('/partner'); return; }
      setVehicle(found);

      // Pre-fill insurance/PUC data from seller's declarations
      setInsurancePUCData(prev => ({
        ...prev,
        verified_insurance_company: found.insurance_company || '',
        verified_insurance_expiry: found.insurance_expiry || '',
        verified_puc_expiry: found.puc_expiry || '',
        insurance_valid: found.has_insurance,
        puc_valid: found.has_puc,
      }));

      try {
        const formRes = await axios.get(`${API}/verification-form/${found.vehicle_type}`, { headers: { Authorization: `Bearer ${token}` } });
        setVerificationForm(formRes.data);
      } catch {}
    } catch {
      toast.error('Failed to load vehicle');
      navigate('/partner');
    } finally { setLoading(false); }
  };

  const handleChecklistChange = (field, value) => {
    setChecklistData(prev => ({ ...prev, [field]: value }));
  };

  const validateInsurancePUCStep = () => {
    if (insurancePUCData.insurance_valid === null) {
      toast.error('Please validate insurance status');
      return false;
    }
    if (insurancePUCData.puc_valid === null) {
      toast.error('Please validate PUC status');
      return false;
    }
    if (insurancePUCData.insurance_valid && !insurancePUCData.insurance_photo) {
      toast.error('Please upload the insurance document photo');
      return false;
    }
    if (insurancePUCData.puc_valid && !insurancePUCData.puc_photo) {
      toast.error('Please upload the PUC certificate photo');
      return false;
    }
    return true;
  };

  const handleSubmitAll = async () => {
    if (!selfiePhoto) { toast.error('Selfie photo is required'); return; }
    if (!vehiclePhotos.full || !vehiclePhotos.plate || !vehiclePhotos.odometer) { toast.error('All 3 vehicle photos are required'); return; }
    if (!ownerPhoto) { toast.error('Owner photo is required'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/submit-inspection`, {
        vehicle_id: vehicleId,
        arrival_photo: selfiePhoto,
        with_vehicle_photo: vehiclePhotos.full,
        with_owner_photo: ownerPhoto,
        checklist_data: {
          ...checklistData,
          vehicle_photos: vehiclePhotos,
          form_answers: formAnswers,
          travel_started_at: travelStartTime,
          arrived_at: arrivedTime,
          // NEW: insurance & PUC verification data
          insurance_puc_verification: {
            insurance_photo: insurancePUCData.insurance_photo,
            puc_photo: insurancePUCData.puc_photo,
            insurance_valid: insurancePUCData.insurance_valid,
            puc_valid: insurancePUCData.puc_valid,
            verified_insurance_company: insurancePUCData.verified_insurance_company,
            verified_insurance_expiry: insurancePUCData.verified_insurance_expiry,
            verified_puc_expiry: insurancePUCData.verified_puc_expiry,
            insurance_company_mismatch: insurancePUCData.insurance_company_mismatch,
            insurance_expiry_mismatch: insurancePUCData.insurance_expiry_mismatch,
            puc_mismatch: insurancePUCData.puc_mismatch,
            puc_expiry_mismatch: insurancePUCData.puc_expiry_mismatch,
          }
        }
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Inspection submitted! Vehicle marked as verified.');
      navigate('/partner');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  if (!vehicle) return null;

  const checklistFields = getChecklistFields(vehicle.vehicle_type, vehicle.ev_subtype);
  const progress = (step / TOTAL_STEPS) * 100;

  const photoBoxes = [
    { key: 'full', label: 'Full Vehicle Photo', desc: 'Clear front/side view of the entire vehicle', icon: '🚗' },
    { key: 'plate', label: 'Number Plate / HSRP', desc: 'Clear photo of registration plate', icon: '🔢' },
    { key: 'odometer', label: 'Driver Console / Odometer', desc: 'Turn on the console and capture total KMs run', icon: '⏱️' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cities={[]} selectedCity="" setSelectedCity={() => {}} showSearch={false} />

      {/* Sticky progress bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate('/partner')}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition">
              <ArrowLeft className="h-4 w-4" />
              {step > 1 ? 'Back' : 'Dashboard'}
            </button>
            <div className="text-center">
              <p className="text-xs text-orange-500 font-semibold uppercase tracking-wide">Step {step} of {TOTAL_STEPS}</p>
              <p className="text-sm font-bold text-gray-800">{STEP_LABELS[step - 1]}</p>
            </div>
            <div className="text-xs text-gray-400 font-mono">{Math.round(progress)}%</div>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i < step ? 'bg-orange-500' : 'bg-gray-200'}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 pb-20">

        {/* STEP 1: Vehicle & Seller Details */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
              <p className="text-xs font-bold text-orange-600 uppercase mb-3 tracking-wide">Vehicle Information</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500 text-xs">Type</span><p className="font-semibold capitalize mt-0.5">{vehicle.vehicle_type}</p></div>
                <div><span className="text-gray-500 text-xs">Vehicle</span><p className="font-semibold mt-0.5">{vehicle.brand_name} {vehicle.model_name}</p></div>
                <div><span className="text-gray-500 text-xs">Color</span><p className="font-semibold mt-0.5">{vehicle.color || 'N/A'}</p></div>
                <div><span className="text-gray-500 text-xs">Reg. No.</span><p className="font-semibold font-mono mt-0.5">{vehicle.registration_no}</p></div>
                {vehicle.vehicle_display_id && (
                  <div className="col-span-2"><span className="text-gray-500 text-xs">Vehicle ID</span><p className="font-semibold font-mono text-blue-600 mt-0.5">{vehicle.vehicle_display_id}</p></div>
                )}
              </div>
            </div>

            {/* Insurance & PUC preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <p className="text-xs font-bold text-blue-600 uppercase mb-3 tracking-wide">Seller's Insurance & PUC Data (to verify)</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className={`p-3 rounded-xl border ${vehicle.has_insurance ? 'bg-white border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                  <p className="text-xs text-gray-400">Insurance</p>
                  <p className={`font-semibold mt-0.5 ${vehicle.has_insurance ? 'text-blue-700' : 'text-gray-500'}`}>
                    {vehicle.has_insurance ? `✅ ${vehicle.insurance_company}` : '❌ No Insurance'}
                  </p>
                  {vehicle.insurance_expiry && <p className="text-xs text-blue-500 mt-0.5">Exp: {new Date(vehicle.insurance_expiry).toLocaleDateString('en-IN')}</p>}
                </div>
                <div className={`p-3 rounded-xl border ${vehicle.has_puc ? 'bg-white border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <p className="text-xs text-gray-400">PUC</p>
                  <p className={`font-semibold mt-0.5 ${vehicle.has_puc ? 'text-green-700' : 'text-gray-500'}`}>
                    {vehicle.has_puc ? '✅ Valid PUC' : '❌ No PUC'}
                  </p>
                  {vehicle.puc_expiry && <p className="text-xs text-green-500 mt-0.5">Exp: {new Date(vehicle.puc_expiry).toLocaleDateString('en-IN')}</p>}
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <p className="text-xs font-bold text-blue-600 uppercase mb-3 tracking-wide">Seller Details</p>
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-500 text-xs">Name</span><p className="font-semibold mt-0.5">{vehicle.seller_name || 'N/A'}</p></div>
                <div><span className="text-gray-500 text-xs">City</span><p className="font-semibold mt-0.5">{vehicle.city}</p></div>
                {vehicle.contact_phone && (
                  <a href={`tel:+91${vehicle.contact_phone}`}
                    className="flex items-center gap-2 bg-green-500 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-green-600 transition w-fit mt-2">
                    <Phone className="h-4 w-4" />
                    Call Seller: +91 {vehicle.contact_phone}
                  </a>
                )}
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
              Proceed to Travel Confirmation <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}

        {/* STEP 2: Start Travel */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="text-center py-8">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Car className="h-12 w-12 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Ready to Travel?</h2>
              <p className="text-gray-500 text-sm max-w-sm mx-auto">Tap the button once you start travelling to the vehicle location.</p>
            </div>
            {travelStartTime ? (
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 text-center">
                <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-bold">Travel Started!</p>
                <p className="text-green-600 text-sm">Started at: {new Date(travelStartTime).toLocaleTimeString('en-IN')}</p>
              </div>
            ) : (
              <button onClick={() => { setTravelStartTime(new Date().toISOString()); toast.success('Travel started!'); }}
                className="w-full py-5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-lg transition flex items-center justify-center gap-3">
                <Car className="h-6 w-6" /> I Have Started Travelling
              </button>
            )}
            {travelStartTime && (
              <Button onClick={() => setStep(3)} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
                Next: Confirm Arrival <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        )}

        {/* STEP 3: Arrival */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="text-center py-8">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <MapPin className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Have You Arrived?</h2>
              <p className="text-gray-500 text-sm">Tap once you have reached the vehicle location.</p>
            </div>
            {arrivedTime ? (
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 text-center">
                <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-bold">Arrival Confirmed!</p>
                <p className="text-green-600 text-sm">Arrived at: {new Date(arrivedTime).toLocaleTimeString('en-IN')}</p>
              </div>
            ) : (
              <button onClick={() => { setArrivedTime(new Date().toISOString()); toast.success('Arrival confirmed!'); }}
                className="w-full py-5 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-bold text-lg transition flex items-center justify-center gap-3">
                <MapPin className="h-6 w-6" /> I Have Reached the Location
              </button>
            )}
            {arrivedTime && (
              <Button onClick={() => setStep(4)} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
                Next: Take Selfie <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        )}

        {/* STEP 4: Selfie */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
              <strong>Important:</strong> You must take a live selfie at the location. Gallery photos are not accepted.
            </div>
            {selfiePhoto ? (
              <div className="relative">
                <img src={selfiePhoto} alt="Selfie" className="w-full h-64 object-cover rounded-2xl" />
                <button onClick={() => setSelfiePhoto(null)}
                  className="absolute top-3 right-3 w-9 h-9 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md">
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-3 left-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" /> Selfie captured
                </div>
              </div>
            ) : (
              <label className="block cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:border-orange-400 hover:bg-orange-50/30 transition">
                  <Camera className="h-14 w-14 text-gray-400 mx-auto mb-3" />
                  <p className="font-semibold text-gray-700 mb-1">Take a Live Selfie</p>
                  <p className="text-xs text-gray-500 mb-4">Use your front camera at the vehicle location</p>
                  <span className="inline-block px-6 py-3 bg-orange-500 text-white rounded-xl text-sm font-semibold">📷 Open Camera</span>
                </div>
                <Input type="file" accept="image/*" capture="user" className="hidden"
                  onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onloadend = () => setSelfiePhoto(r.result); r.readAsDataURL(f); }} />
              </label>
            )}
            {selfiePhoto && (
              <Button onClick={() => setStep(5)} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
                Next: Vehicle Photos <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        )}

        {/* STEP 5: Vehicle Photos */}
        {step === 5 && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-800">
              All 3 photos are mandatory. Please capture them at the vehicle location.
            </div>
            {photoBoxes.map(box => (
              <PhotoCaptureBox
                key={box.key}
                label={box.label}
                desc={box.desc}
                icon={box.icon}
                value={vehiclePhotos[box.key]}
                onCapture={val => setVehiclePhotos(p => ({ ...p, [box.key]: val }))}
                onClear={() => setVehiclePhotos(p => ({ ...p, [box.key]: null }))}
              />
            ))}
            {vehiclePhotos.full && vehiclePhotos.plate && vehiclePhotos.odometer && (
              <Button onClick={() => setStep(6)} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
                Next: Insurance & PUC Verification <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        )}

        {/* STEP 6: Insurance & PUC Verification (NEW) */}
        {step === 6 && (
          <div className="space-y-4">
            <InsurancePUCVerificationStep
              vehicle={vehicle}
              insurancePUCData={insurancePUCData}
              setInsurancePUCData={setInsurancePUCData}
            />
            <Button
              onClick={() => {
                if (validateInsurancePUCStep()) setStep(7);
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
              Next: Verification Form <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}

        {/* STEP 7: Verification Form */}
        {step === 7 && (
          <div className="space-y-6">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm text-orange-800">
              Vehicle Type: <strong className="capitalize">{vehicle.vehicle_type}{vehicle.ev_subtype ? ` (${vehicle.ev_subtype})` : ''}</strong>
            </div>

            {verificationForm?.questions?.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 border-b pb-2">Verification Questions</h3>
                {verificationForm.questions.map((q, idx) => (
                  <div key={q.id || idx} className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-sm font-medium text-gray-800 mb-3">{q.question}</p>
                    {q.type === 'km_run' ? (
                      <Input type="text" placeholder="e.g. 45,000"
                        value={formAnswers[q.id || idx] || ''}
                        onChange={e => setFormAnswers(p => ({ ...p, [q.id || idx]: e.target.value.replace(/[^0-9,]/g, '') }))}
                        className="w-full" />
                    ) : (
                      <div className="flex gap-6">
                        {['Yes', 'No'].map(opt => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" name={`q-${q.id || idx}`} value={opt}
                              checked={formAnswers[q.id || idx] === opt}
                              onChange={() => setFormAnswers(p => ({ ...p, [q.id || idx]: opt }))}
                              className="accent-orange-500" />
                            <span className={`text-sm font-medium ${opt === 'Yes' ? 'text-green-600' : 'text-red-600'}`}>{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <ChecklistForm fields={checklistFields} data={checklistData} onChange={handleChecklistChange} />

            <Button onClick={() => {
              if (verificationForm?.questions) {
                const unanswered = verificationForm.questions.filter(q => !formAnswers[q.id || verificationForm.questions.indexOf(q)]);
                if (unanswered.length > 0) { toast.error('Please answer all verification questions'); return; }
              }
              setStep(8);
            }} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base font-semibold">
              Next: Owner Photo <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}

        {/* STEP 8: Owner Photo & Submit */}
        {step === 8 && (
          <div className="space-y-5">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
              <strong>Note:</strong> This image is for internal verification only and will not be displayed publicly.
            </div>

            <div className="text-center mb-2">
              <h3 className="font-bold text-gray-800 text-lg mb-1">Photo with Vehicle Owner</h3>
              <p className="text-sm text-gray-500">Take a photo of the vehicle owner standing beside the vehicle.</p>
            </div>

            {ownerPhoto ? (
              <div className="relative">
                <img src={ownerPhoto} alt="Owner" className="w-full h-64 object-cover rounded-2xl" />
                <button onClick={() => setOwnerPhoto(null)}
                  className="absolute top-3 right-3 w-9 h-9 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="block cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center hover:border-orange-400 hover:bg-orange-50/30 transition">
                  <Camera className="h-14 w-14 text-gray-400 mx-auto mb-3" />
                  <p className="font-semibold text-gray-700 mb-1">Capture Owner Photo</p>
                  <span className="inline-block px-6 py-3 bg-orange-500 text-white rounded-xl text-sm font-semibold">📷 Open Camera</span>
                </div>
                <Input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onloadend = () => setOwnerPhoto(r.result); r.readAsDataURL(f); }} />
              </label>
            )}

            {ownerPhoto && (
              <>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-1.5 text-sm">
                  <p className="font-bold text-green-800 mb-2">✅ Ready to Submit</p>
                  {[
                    { label: 'Selfie captured', done: !!selfiePhoto },
                    { label: 'Vehicle photos (3)', done: !!(vehiclePhotos.full && vehiclePhotos.plate && vehiclePhotos.odometer) },
                    { label: 'Insurance document photo', done: !!(insurancePUCData.insurance_photo || !insurancePUCData.insurance_valid) },
                    { label: 'PUC certificate photo', done: !!(insurancePUCData.puc_photo || !insurancePUCData.puc_valid) },
                    { label: 'Checklist completed', done: Object.keys(checklistData).length > 0 },
                    { label: 'Owner photo', done: !!ownerPhoto },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 ${item.done ? 'text-green-700' : 'text-amber-600'}`}>
                      {item.done ? <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                      {item.label}
                    </div>
                  ))}
                </div>

                <Button onClick={handleSubmitAll} disabled={submitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-4 text-lg font-bold rounded-2xl">
                  {submitting
                    ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Submitting Inspection…</>
                    : <><Shield className="h-5 w-5 mr-2" />Finish & Submit Inspection</>
                  }
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PartnerInspectPage;